import { Component, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthenticationService } from '../services/authentication.service';
import { EasyNotificationService } from 'src/app/services/easy-notification.service';
import { WebApiClient } from 'src/app/services/web-api-client.service';
import { WebApiService } from 'src/app/services/web-api.service';
import { UrlHelper } from 'src/app/shared/helpers/url-helper';
import { LocalPrintService } from 'src/app/services/local-print.service';
import { CookieService } from 'ngx-cookie-service';
import { BaseConfig } from 'src/app/services/base-config.service';
import { UserCache } from 'src/app/services/user-cache';
import { Clinic } from 'src/app/system/clinic/models/clinic';
import { CompanyApi } from 'src/app/services/api-service/company/company-api';
import { environment } from 'src/environments/environment';
import { MigrateToolService, MigrationProgress } from '../services/migrate-tool.service';
import { ValueTextPair } from 'src/app/shared/models/value-text-pair';
import { DateHelper } from 'src/app/shared/helpers/date-helper';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ChisWebViewBridgeService } from '../services/chis-webview-bridge.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit, OnDestroy {
  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private notification: EasyNotificationService,
    private webApiClient: WebApiClient,
    private companyApi:CompanyApi,
    private printService: LocalPrintService,
    private cookieService: CookieService,
    private migrationTool:MigrateToolService,
    private ngZone: NgZone,
    private chisWebViewBridge: ChisWebViewBridgeService
  ) {
    this.route.queryParams.subscribe(params => {
      const isAutoLoginClinic = '' + params['isAutoLoginClinic'];
      if (isAutoLoginClinic) {
        // 需明確指定，不然以系統預設參數為準
        if (isAutoLoginClinic.toUpperCase() === 'Y' || isAutoLoginClinic.toUpperCase() === 'TRUE') {
          this.isAutoLoginClinic = true;
        } else if (isAutoLoginClinic.toUpperCase() === 'N' || isAutoLoginClinic.toUpperCase() === 'FALSE') {
          this.isAutoLoginClinic = false;
        }
      }
    });
  }
  isPreDemo = environment.envName == 'pre-demo';//||true;
  stockApi: WebApiService;

  loginForm: FormGroup;

  loginMessage = '';

  isLoadingCompanyName = true;
  isLoadCompanyNameFailed = false;

  returnUrl: string;
  companyCode = '';
  clinics: Clinic[];
  logoutReason: string;
  openLoginConfirm = false;
  loginConfirmMessage = '';
  isAutoLoginClinic: boolean = BaseConfig.getConfig().env.isAutoLoginClinic;
  private removeChisMessageListener: () => void;
  private autoLoginWhenSingleClinic = false;
  private chisCredentialsReceived = false;

  lastLoginCompanyCode = '';
  lastLoginSuccessUserName = '';
  lastLoginSuccessClinicCode = '';
  companyName = '';
  isChrome = true;
  orgs: ValueTextPair[];
  open2fv = false;
  private getDefaultPage(): string {
    // A.有returnUrl
    if (this.returnUrl && this.returnUrl !== '/') {
      return this.returnUrl;
    }
    // B.沒有returnUrl
    return '/' + this.companyCode + BaseConfig.getConfig().env.defaultRoute;
  }

  private checkBrowserType(){
    const userAgent = navigator.userAgent.toLowerCase();
    // console.log('userAgent:', userAgent);
    this.isChrome = this.chisWebViewBridge.isAvailable ||
      (userAgent.includes('chrome') && !userAgent.includes('edg') && !userAgent.includes('opr') && !userAgent.includes('brave'));
    // console.log('isChrome:', isChrome);
  }

  checkFontSize(name:string){
    const length = name.length;

    if (length >= 14) {
      return 25.5;
    } else if (length === 13) {
      return 27.5;
    } else if (length === 12) {
      return 29.5;
    } else if (length === 11) {
      return 32;
    } else {
      return 36; // 10個字及以下 (預設)
    }
  }

  ngOnInit() {
    this.checkBrowserType();
    
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      clinicCode: [''],
      isAutoLoginClinic: [this.isAutoLoginClinic]
    });
    this.loginForm.valueChanges.subscribe(v=>{
      this.clearLoginMessage()
    });
    this.removeChisMessageListener = this.chisWebViewBridge.listen(message => {
      this.ngZone.run(() => this.handleChisWebViewMessage(message));
    });
    // get company code
    this.route.paramMap.subscribe(async (q) => {
      this.companyCode = UrlHelper.getCompanyCodeFromRouteParamMap(q);
      if (this.authenticationService.isLoginToCompany(this.companyCode) && !this.chisWebViewBridge.isAvailable) {
        const url = this.getDefaultPage();
        this.router.navigate([url]);
      } else {
        if(this.companyCode == '_'){
          await this.companyApi.CreateStd();
        }
        this.getCompanyName(this.companyCode);
      }
    });

    // 偵測使用者地理位置（優先使用裝置 GPS，失敗則回退至 IP 位置）
    this.detectLocation().catch(err => {
      console.warn('[geo] Location detect failed', err);
    });
  }

  getMsg(msgId: string): string {
    return this.notification.getTextById(msgId);
  }

  ngOnDestroy() {
    if (this.removeChisMessageListener) {
      this.removeChisMessageListener();
    }
  }

  private handleChisWebViewMessage(message: any) {
    if (!message) {
      return;
    }
    if (message.type === 'chis-login-context') {
      if (message.companyCode === this.companyCode) {
        this.chisWebViewBridge.setLoginContext({
          sessionId: message.sessionId,
          companyCode: message.companyCode,
          remember: !!message.remember
        });
      }
      return;
    }
    if (message.type !== 'chis-login-data' || message.companyCode !== this.companyCode) {
      return;
    }
    if (this.chisCredentialsReceived) {
      this.chisWebViewBridge.postMessage({ type: 'chis-login-data-received', companyCode: this.companyCode });
      return;
    }
    if (!message.account || !message.password) {
      return;
    }

    this.chisWebViewBridge.setLoginContext({
      sessionId: message.sessionId,
      companyCode: message.companyCode,
      remember: !!message.remember
    });
    this.chisCredentialsReceived = true;
    this.isAutoLoginClinic = false;
    this.loginForm.patchValue({
      username: message.account,
      password: message.password,
      isAutoLoginClinic: false
    }, { emitEvent: false });
    this.autoLoginWhenSingleClinic = true;
    this.chisWebViewBridge.postMessage({ type: 'chis-login-data-received', companyCode: this.companyCode });
    this.onAccountChange();
  }

  // 進行登入到某診所
  processClinicLogin() {
    this.clearLoginMessage();
    // get return url from route parameters or default to '/'
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    this.logoutReason = this.route.snapshot.queryParams['reason'];
    if (this.logoutReason === '401') {
      this.notification.showErrorById('MSG_LoginReason401');
    }

    this.lastLoginCompanyCode = this.cookieService.get('LastLoginCompanyCode');
    this.lastLoginSuccessUserName = this.cookieService.get('LastLoginSuccessUserName');
    this.lastLoginSuccessClinicCode = this.cookieService.get('LastLoginSuccessClinicCode');

    // 相同公司碼才設定user與clinic
    if (this.companyCode && this.companyCode === this.lastLoginCompanyCode) {
      if (this.lastLoginSuccessUserName !== '') {
        this.loginForm.controls['username'].setValue(this.lastLoginSuccessUserName);
        this.onAccountBlur();
      }
    }
  }

  get loginFC() { return this.loginForm.controls; }

  async login(force: boolean) {
    this.loginForm.disable();
    this.clearLoginMessage();

    this.loginMessage = this.getMsg('MSG_SystemLoginLogging');
    var clinicCode = this.loginFC.clinicCode.value;
    var clinic = this.clinics.find(c=>c.Code == clinicCode);
    try{
      if(this.isPreDemo){
        var msg = await this.migrationTool.checkDataCount(this.companyCode,clinic.Id);
        if(msg){
          this.loginMessage = '';
          this.loginConfirmMessage = msg;
          this.loginForm.enable({emitEvent:false});
          return
        }
      }
      this.stageChisLoginAttempt();
      var data = await this.authenticationService.login(
                        this.loginFC.username.value, this.loginFC.password.value,
                        this.companyCode, clinic, force, this.loginFC.isAutoLoginClinic.value
                        );
      this.clearLoginMessage();
      this.loginForm.enable();
      if (data) {
        const url = this.getDefaultPage();
        // 注意return url必須清除，不然切換company時，return url會是前一個company
        this.returnUrl = '';
        this.router.navigate([url]);
        await this.afterLogin();
      } else {
        this.loginMessage = this.getMsg('MSG_SystemLoginFailed');
      }
    }catch(error){
      if(error.message.startsWith('2FV-')){
        var token = error.message.split(';')[1];
        this.open2fv = true;
        var validateResult = false;
        for(let i =0; i<5;i++){
          var code = await new Promise<string>((rv,rj)=>{
            this.result2FV = rv;
          })

          var validateResult = await this.authenticationService.validateCodeLogin(token,code,clinic,this.loginFC.password.value);
          if (validateResult) {
            this.open2fv = false;
            break;
          }else{
            this.errorMsg2FV = `驗證碼錯誤(${i+1})次`
          }
        }
        this.clearLoginMessage();
        if (validateResult) {
          const url = this.getDefaultPage();
          // 注意return url必須清除，不然切換company時，return url會是前一個company
          this.returnUrl = '';
          this.router.navigate([url]);
          await this.afterLogin();
        } else {
          this.open2fv = false;
          this.openLoginConfirm = true;
          this.loginConfirmMessage = '驗證失敗';
        }

        this.loginForm.enable({emitEvent:false});
      }else{
        this.clearLoginMessage();
        if (error.code === 'AUT004') {
          this.openLoginConfirm = true;
          this.loginConfirmMessage = error.message;
        } else if (error.code === 'AUT005') {
          this.loginMessage = this.getMsg('MSG_SystemLoginFailed');
        } else if (error.code === 'AUT006') {
          this.loginMessage = this.getMsg('MSG_SystemLoginWorkingCheck');
        } else if (error.code === 'AUT007') {
          this.loginMessage = this.getMsg('MSG_SystemLoginCompanyNotEffective');
        } else if (error.code === 'AUT008') {
          this.loginMessage = this.getMsg('MSG_SystemLoginClinicNotEffective');
        } else {
          this.loginMessage = this.getMsg('MSG_SystemLoginFailed');
        }
        this.loginForm.enable({emitEvent:false});
      }
    }
  }

  private stageChisLoginAttempt() {
    const context = this.chisWebViewBridge.getLoginContext();
    if (!this.chisWebViewBridge.isAvailable || !context || context.companyCode !== this.companyCode) {
      return;
    }
    this.chisWebViewBridge.postMessage({
      type: 'chis-login-attempt',
      sessionId: context.sessionId,
      companyCode: this.companyCode,
      account: this.loginFC.username.value,
      password: this.loginFC.password.value,
      remember: context.remember
    });
  }
  result2FV:(string)=>void = null;
  errorMsg2FV = '';
  close2fv(){
    this.result2FV= null;
    this.open2fv = false;
    this.openLoginConfirm = true;
    this.clearLoginMessage();
    //this.loginConfirmMessage = '驗證取消';
    this.loginForm.enable({emitEvent:false});
  }
  validate(code){
    if(this.result2FV){
      this.result2FV(code)
      this.result2FV= null;
    }
  }

  onValidateInput(evt:KeyboardEvent){
    if(evt.key=='Enter'){
      this.validate((evt.target as HTMLInputElement).value);
    }
  }
  async afterLogin() {
    this.stockApi = this.webApiClient.createHisService('stock/stockItem');

    await this.getLowerSafeStockItemsInfo();
    //this.printService.initialPrintClient();
    this.cookieService.set('LastLoginCompanyCode', this.companyCode, 7);
    this.cookieService.set('LastLoginSuccessUserName', this.loginFC.username.value, 7);
    this.cookieService.set('LastLoginSuccessClinicCode', this.loginFC.clinicCode.value, 7);

  }
  private async getLowerSafeStockItemsInfo() {
    try{
      var data = await this.stockApi.get('getLowerSafeStockItemsInfo').toPromise()
      if (data.isShow) {
        (data.messages as Array<string>).forEach(message => {
          this.notification.showError(message, false);
        });
      }
    }catch(error){
      this.notification.showGetDataFailed();
    }
  }

  private getCompanyName(companyCode: string) {
    this.clearLoginMessage();
    this.isLoadingCompanyName = true;
    this.authenticationService.getCompanyName(companyCode)
      .subscribe(
        async (data) => {
          this.companyName = data.companyName;
          if (this.companyName) {
            this.isLoadingCompanyName = false;
            this.processClinicLogin();
            this.chisWebViewBridge.postMessage({ type: 'chis-login-ready', companyCode: this.companyCode });
          } else {
            await this.toMigrationState();
          }
        },
        (error) => {
          this.clearLoginMessage();
          this.isLoadCompanyNameFailed = true;
        });
  }

  // 位置偵測主流程：優先使用 Geolocation API，失敗時使用 IP-based lookup
  private async detectLocation(): Promise<any> {
    try {
      if (navigator && 'geolocation' in navigator) {
        const pos: any = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
        });
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        console.log('[geo] coords', { lat, lon, accuracy: pos.coords.accuracy });
        const rev = await this.reverseGeocode(lat, lon);
        console.log('[geo] reverse geocode result', rev);
        return rev;
      } else {
        console.warn('[geo] navigator.geolocation not available, falling back to IP');
        const ip = await this.getLocationViaIP();
        return ip;
      }
    } catch (err) {
      console.warn('[geo] geolocation error, fallback to IP', err);
      try {
        const ip = await this.getLocationViaIP();
        return ip;
      } catch (e) {
        console.warn('[geo] ip fallback failed', e);
        throw e;
      }
    }
  }

  private async reverseGeocode(lat: number, lon: number): Promise<any> {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&accept-language=zh-TW`;
      const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!resp.ok) throw new Error('reverse geocode http error ' + resp.status);
      const data = await resp.json();
      const addr = (data && data.address) ? data.address : {};
      const county = addr.county || addr.state || addr.city || addr.region || '';
      const district = addr.city_district || addr.suburb || addr.town || addr.village || addr.hamlet || '';
      const road = addr.road || addr.pedestrian || addr.cycleway || '';
      const number = addr.house_number || addr.house_no || '';
      const countyDistrict = county + district;  // e.g. "高雄市那瑪夏區"
      const display = data.display_name || [county, district, road, number].filter(x => x).join(' ');
      return { lat, lon, county, district, countyDistrict, road, number, display, raw: data };
    } catch (err) {
      console.warn('[geo] reverseGeocode failed', err);
      return { lat, lon, county: '', district: '', countyDistrict: '' };
    }
  }

  private async getLocationViaIP(): Promise<any> {
    try {
      const resp = await fetch('https://ipapi.co/json/');
      if (!resp.ok) throw new Error('ip lookup http error ' + resp.status);
      const data = await resp.json();
      const lat = data.latitude || data.lat || null;
      const lon = data.longitude || data.lon || null;
      // 用 Nominatim 反查中文地址
      let rev: any = null;
      if (lat && lon) {
        rev = await this.reverseGeocode(lat, lon);
      }
      const out = {
        ip: data.ip,
        city: data.city,
        region: data.region,
        region_code: data.region_code,
        country: data.country_name,
        latitude: lat,
        longitude: lon,
        postal: data.postal,
        // 中文地址（來自 Nominatim）
        county: rev ? rev.county : '',
        district: rev ? rev.district : '',
        countyDistrict: rev ? rev.countyDistrict : '',
        road: rev ? rev.road : '',
        number: rev ? rev.number : '',
        display: rev ? rev.display : '',
        raw: data
      };
      console.log('[geo] ip fallback result (with Chinese reverse geocode)', out);
      return out;
    } catch (err) {
      console.warn('[geo] getLocationViaIP failed', err);
      throw err;
    }
  }

  clearLoginMessage() {
    this.loginMessage = '';
    this.loginConfirmMessage = '';
  }
  onLoginClick() {
    this.clearLoginMessage();
    if (this.isAutoLoginClinic) {
    } else {
      if (this.clinics === undefined || this.clinics.length <= 0) {
        this.loginMessage = this.getMsg('MSG_SystemLoginNoClinic');
        return;
      }
      if (this.loginFC.clinicCode.value === '') {
        this.loginMessage = this.getMsg('MSG_SystemLoginNoClinicSelected');
        return;
      }
    }
    if (this.loginForm.invalid) {
      this.loginMessage = this.getMsg('MSG_SystemLoginInputError');
      return;
    }

    this.login(false);
  }

  onAccountChange() {
    if (this.isLoadingCompanyName || this.isLoadCompanyNameFailed) {
      return;
    }
    this.clearLoginMessage();
    if (this.isAutoLoginClinic) {
      return;
    }
    this.loginMessage = this.getMsg('MSG_SystemLoginFetchingClinics');
    this.authenticationService.getClinics(this.companyCode, this.loginFC.username.value)
      .subscribe(
        (data) => {
          this.clearLoginMessage();
          if (!data || data.length === 0) {
            this.loginMessage = this.getMsg('MSG_SystemLoginAccountNoClinic');
          }
          this.clinics = Array.isArray(data) ? data : [];
          if (this.autoLoginWhenSingleClinic && this.clinics.length === 1) {
            this.autoLoginWhenSingleClinic = false;
            this.loginForm.controls['clinicCode'].setValue(this.clinics[0].Code);
            this.login(false);
            return;
          }
          this.autoLoginWhenSingleClinic = false;
          if (this.clinics.length > 0) {

            if (this.lastLoginSuccessUserName !== '') {
              if (this.lastLoginSuccessUserName === this.loginFC.username.value) {
                if (this.lastLoginSuccessClinicCode !== '') {
                  this.loginForm.controls['clinicCode'].setValue(this.lastLoginSuccessClinicCode);
                }
                else {
                  this.loginForm.controls['clinicCode'].setValue(this.clinics[0].Code);
                }
              }
              else {
                this.loginForm.controls['clinicCode'].setValue(this.clinics[0].Code);
              }
            }
            else {
              this.loginForm.controls['clinicCode'].setValue(this.clinics[0].Code);
            }
          }

        },
          (error) => {
          this.autoLoginWhenSingleClinic = false;
          this.loginMessage = this.getMsg('MSG_SystemLoginFetchClinicsFailed');
        },
        () => {
        });
  }
  onAccountBlur() {
    if (!this.clinics) {
      this.onAccountChange();
    }
  }
  onConfirmResult(result: boolean) {
    if (result) {
      this.login(true);
    }
    this.openLoginConfirm = false;
  }
  capsLockEnabled:boolean;
  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (typeof event.getModifierState === 'function') {
      this.capsLockEnabled = event.getModifierState('CapsLock');
    } else {
      this.capsLockEnabled = false;
    }
  }



  onMigrateLoginClick() {
    if(!this.isPreDemo){
      return;
    }
    this.clearLoginMessage();
    if (this.loginForm.invalid) {
      this.loginMessage = this.getMsg('MSG_SystemLoginInputError');
      return;
    }

    this.migrateLogin();
  }
  mg:MigrationProgress = null;
  toolLoginSuccessed = false;
  progressTracing = null;
  counter = null;
  nextUpdateTime = new Date();
  nextSecond = 30;
  async migrateLogin() {
    this.loginForm.disable();
    this.clearLoginMessage();
    try{
      this.toolLoginSuccessed = await this.migrationTool.login(this.loginForm.value.clinicCode,this.loginForm.value.username,this.loginForm.value.password);
      this.clearLoginMessage();
      this.loginForm.enable();
      if (this.toolLoginSuccessed) {
        this.mg = await this.migrationTool.getProgress(this.companyCode);
      } else {
        this.loginMessage = this.getMsg('MSG_SystemLoginFailed');
      }
    }catch(error){

      this.loginForm.enable({emitEvent:false});
    }
  }
  copyConnStr(){
    navigator.clipboard.writeText(this.mg.ConnectionString);
    // Alert the copied text
    alert("連接字串已複製到剪貼簿");
  }
  async onMigrateCreateClick(createBtn:HTMLButtonElement){
    createBtn.disabled = true;
    var ret = await this.migrationTool.createDb(this.companyCode);
    await this.updateState();
    createBtn.disabled = false;
  }
  async onMigrateDeleteClick(deleteBtn:HTMLButtonElement){

    deleteBtn.disabled = true;
    var ret = await this.migrationTool.deleteDb(this.companyCode);
    window.location.reload();
  }
  async updateState(){
    this.mg = await this.migrationTool.getProgress(this.companyCode);
    if(this.progressTracing){
      clearInterval(this.progressTracing);
    }
    this.nextUpdateTime = DateHelper.addSecond(new Date(),30);
    this.progressTracing = setInterval(async ()=>{
      this.nextUpdateTime = DateHelper.addSecond(new Date(),30);
      this.mg = await this.migrationTool.getProgress(this.companyCode);
      if(this.mg.State =='DB Created'){
        clearInterval(this.progressTracing);
        this.progressTracing = null;
        clearInterval(this.counter);
        this.counter = null;
      }
    },10000);
    if(this.counter){
      clearInterval(this.counter);
    }
    this.counter = setInterval(()=>{
      this.nextSecond = Math.floor((this.nextUpdateTime.valueOf() - new Date().valueOf())/1000)
    },1000)
  }
  async onMigrateToDemoClick(evt:MatCheckboxChange){
    if(evt.source.disabled){
      return;
    }
    evt.source.setDisabledState(true);
    var ret = await this.migrationTool.setReady(this.companyCode,evt.checked)
    evt.source.setDisabledState(false)
    if(ret){
      this.mg = await this.migrationTool.getProgress(this.companyCode);
    }else{
      alert('失敗!!~')
    }
  }

  async toMigrationState(){
    this.isLoadCompanyNameFailed = true;
    if(this.isPreDemo){
      try{
      this.orgs = await this.migrationTool.getOrganizes();
      }catch(e){
        console.log(e)
      }
    }
  }
}
